import React from 'react';
import { motion } from 'framer-motion';
// eslint-disable-next-line no-unused-vars
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Contact from '../components/Contact';
import WhyChooseUs from '../components/WhyChooseUs';

const AboutUs = () => {
  const { t } = useTranslation('about');

  const teamMembers = [
    {
      name: 'Martin Roborgh',
      roleKey: 'team.members.martin.role',
      bioKey: 'team.members.martin.bio',
      image: '/images/portraits/Martin_roborgh.webp',
    },
    {
      name: 'Yaroslav Haidash',
      roleKey: 'team.members.yaroslav.role',
      bioKey: 'team.members.yaroslav.bio',
      image: '/images/portraits/yaroslav_haidash.webp',
    },
    {
      name: 'Ziad Yassine',
      roleKey: 'team.members.ziad.role',
      bioKey: 'team.members.ziad.bio',
      image: '/images/portraits/Ziad_Yassine.webp',
    },
  ];

  const timelineKeys = ['2022a', '2022b', '2023', '2024', '2025', '2026'];
  const timelineYears = ['2022', '2022', '2023', '2024', '2025', '2026'];

  const timelineIcons = [
    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" /></svg>,
    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z" /></svg>,
    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" /></svg>,
    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>,
    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" /></svg>,
    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 10h4.764a2 2 0 011.789 2.894l-3.5 7A2 2 0 0115.263 21h-4.017c-.163 0-.326-.02-.485-.06L7 20m7-10V5a2 2 0 00-2-2h-.095c-.5 0-.905.405-.905.905 0 .714-.211 1.412-.608 2.006L7 11v9m7-10h-2M7 20H5a2 2 0 01-2-2v-6a2 2 0 012-2h2.5" /></svg>,
  ];

  const paragraphs = t('intro.paragraphs', { returnObjects: true });

  return (
    <div className="bg-white">
      {/* About Us Section - White Background */}
      <section className="bg-white min-h-screen py-32 flex items-center" data-section="about-intro">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="flex flex-col md:flex-row justify-between items-start gap-6 mb-16"
          >
            <div>
              <p className="text-secondary-accent text-sm font-semibold mb-2 uppercase tracking-wider flex items-center gap-2">
                <span className="w-2 h-2 bg-secondary-accent rounded-full"></span>
                {t('intro.badge')}
              </p>
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-text-primary mb-4">
                {t('intro.title')}
              </h2>
            </div>
            <div className="text-left md:text-right max-w-md">
              <p className="text-base sm:text-lg text-grey-accent">
                {t('intro.description')}
              </p>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-center"
          >
            <div className="space-y-6">
              {Array.isArray(paragraphs) && paragraphs.map((paragraph, index) => (
                <p key={index} className="text-lg sm:text-xl text-grey-accent leading-relaxed">
                  {paragraph}
                </p>
              ))}
            </div>
            <div className="relative h-96 bg-[#F9F9F9] rounded-2xl overflow-hidden">
              <img
                src="/images/about_us/about_us.webp"
                srcSet="/images/about_us/about_us.webp"
                alt="About AppTek"
                className="w-full h-full object-cover"
                loading="lazy"
              />
            </div>
          </motion.div>
        </div>
      </section>

      {/* Why Choose Us Section - Using shared component */}
      <div className="mb-20">
        <WhyChooseUs sectionId="why-choose-us" minHeight={true} />
      </div>

      {/* Meet the Team Section - White Background */}
      <section className="bg-white min-h-screen py-20 pb-32 mb-20 flex items-center" data-section="team">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="flex flex-col md:flex-row justify-between items-start gap-6 mb-16"
          >
            <div>
              <p className="text-secondary-accent text-sm font-semibold mb-2 uppercase tracking-wider flex items-center gap-2">
                <span className="w-2 h-2 bg-secondary-accent rounded-full"></span>
                {t('team.badge')}
              </p>
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-text-primary mb-4">
                {t('team.title')}
              </h2>
            </div>
            <div className="text-left md:text-right max-w-md">
              <p className="text-base sm:text-lg text-grey-accent">
                {t('team.description')}
              </p>
            </div>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {teamMembers.map((member, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="group cursor-pointer h-full"
              >
                <div className="bg-[#F9F9F9] rounded-2xl p-8 group-hover:bg-text-primary transition-all duration-300 relative overflow-hidden flex flex-col hover:scale-[1.02] h-full">
                  {/* Photo Placeholder - Top */}
                  <div className="w-full aspect-square bg-[#EDEDED] rounded-xl mb-6 flex items-center justify-center overflow-hidden">
                    {member.image ? (
                      <img
                        src={member.image}
                        srcSet={member.image.replace(/\.(png|jpg|jpeg)$/i, '.webp')}
                        alt={member.name}
                        className="w-full h-full object-cover"
                        loading="lazy"
                      />
                    ) : (
                      <svg className="w-24 h-24 text-grey-accent/20 group-hover:text-white/20 transition-colors duration-300" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z" />
                      </svg>
                    )}
                  </div>

                  {/* Content */}
                  <div className="flex-grow">
                    <h3 className="text-2xl font-bold text-text-primary group-hover:text-white mb-2 transition-colors duration-300">
                      {member.name}
                    </h3>
                    <p className="text-secondary-accent group-hover:text-white/70 text-sm font-semibold mb-4 uppercase tracking-wider transition-colors duration-300">
                      {t(member.roleKey)}
                    </p>
                    <p className="text-grey-accent group-hover:text-white/80 text-base leading-relaxed transition-colors duration-300">
                      {t(member.bioKey)}
                    </p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Timeline Section - Black Background */}
      <section className="bg-black min-h-screen py-20 pb-32 mb-20 flex items-center" data-section="timeline">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="flex flex-col md:flex-row justify-between items-start gap-6 mb-16"
          >
            <div>
              <p className="text-white/60 text-sm font-semibold mb-2 uppercase tracking-wider flex items-center gap-2">
                <span className="w-2 h-2 bg-white/60 rounded-full"></span>
                {t('timeline.badge')}
              </p>
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-4">
                {t('timeline.title')}
              </h2>
            </div>
            <div className="text-left md:text-right max-w-md">
              <p className="text-base sm:text-lg text-white/70">
                {t('timeline.description')}
              </p>
            </div>
          </motion.div>

          {/* Vertical Timeline */}
          <div className="max-w-4xl mx-auto">
            <div className="relative">
              {/* Vertical connecting line */}
              <div className="absolute left-0 md:left-1/2 top-0 bottom-0 w-0.5 bg-white/20 transform md:-translate-x-1/2"></div>

              {timelineKeys.map((key, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, x: index % 2 === 0 ? -50 : 50 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  className={`relative mb-12 md:mb-16 flex items-center ${index % 2 === 0 ? 'md:flex-row-reverse' : 'md:flex-row'
                    }`}
                >
                  {/* Content */}
                  <div className={`w-full md:w-5/12 ${index % 2 === 0 ? 'md:text-right md:pr-12' : 'md:pl-12'} pl-12 md:pl-0`}>
                    <div className="bg-[#111111] rounded-2xl p-6 group-hover:bg-[#1a1a1a] border border-white/5 hover:border-white/10 transition-all duration-300 cursor-pointer">
                      <div className={`flex items-center gap-3 mb-3 ${index % 2 === 0 ? 'md:justify-end' : 'justify-start'}`}>
                        <div className="px-4 py-1.5 bg-secondary-accent text-white rounded-full text-sm font-bold">
                          {timelineYears[index]}
                        </div>
                      </div>
                      <h3 className="text-2xl font-bold text-white mb-3 transition-colors duration-300">
                        {t(`timeline.items.${key}.title`)}
                      </h3>
                      <p className="text-white/50 text-base leading-relaxed transition-colors duration-300">
                        {t(`timeline.items.${key}.description`)}
                      </p>
                    </div>
                  </div>

                  {/* Timeline Icon - Center */}
                  <div className="absolute left-0 md:left-1/2 transform md:-translate-x-1/2 flex items-center justify-center">
                    <div className="w-12 h-12 bg-secondary-accent rounded-full flex items-center justify-center text-white z-10 shadow-lg">
                      {timelineIcons[index]}
                    </div>
                  </div>

                  {/* Empty space on the opposite side for desktop */}
                  <div className="hidden md:block w-5/12"></div>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <Contact />
    </div>
  );
};

export default AboutUs;
